import type { User } from "@/store/auth.store";
import { getUserAppShell, hasPermission } from "@/lib/authDashboardGate";
import InvitationsIcon from "@/components/icons/InvitationsIcon";
import {
  AddressBookIcon,
  ArchiveBoxIcon,
  ArrowSquareInIcon,
  BinocularsIcon,
  BriefcaseIcon,
  CalculatorIcon,
  ClipboardTextIcon,
  CreditCardIcon,
  CurrencyCircleDollarIcon,
  FilesIcon,
  GearIcon,
  HandshakeIcon,
  HeadsetIcon,
  HouseLineIcon,
  PersonIcon,
  ReceiptIcon,
  SparkleIcon,
  SquaresFourIcon,
  StarIcon,
  UsersIcon,
  WalletIcon,
  WarningIcon,
} from "@phosphor-icons/react";

export enum Role {
  PROPERTY_OWNER = "PROPERTY_OWNER",
  CONTRACTOR = "CONTRACTOR",
  AGENT = "AGENT",
  ADMIN = "ADMIN",
  /** Pre–role-selection / onboarding; not used in sidebar sections. */
  ON_BOARDING = "ON_BOARDING",
}

type SidebarItem = {
  label: string;
  icon: any;
  href: string;
  dot?: boolean;
  permission?: { resource: string; action: "read" | "create" | "update" };
};

type SidebarSection = {
  section: string;
  roles: Role[];
  items: SidebarItem[];
};

export const sidebarConfig: SidebarSection[] = [
  // OWNER
  {
    section: "WORKSPACE",
    roles: [Role.PROPERTY_OWNER],
    items: [
      { label: "Dashboard", icon: SquaresFourIcon, href: "/dashboard" },
      {
        label: "Jobs",
        icon: BriefcaseIcon,
        href: "/dashboard/jobs",
        dot: true,
      },
      {
        label: "Bids",
        icon: InvitationsIcon,
        href: "/dashboard/bids",
        dot: true,
      },
    ],
  },
  {
    section: "NETWORK",
    roles: [Role.PROPERTY_OWNER],
    items: [
      { label: "Contractor", icon: PersonIcon, href: "/dashboard/contractor" },
      {
        label: "Invitations",
        icon: HandshakeIcon,
        href: "/dashboard/invitations",
      },
    ],
  },

  {
    section: "Assets & Feedback",
    roles: [Role.PROPERTY_OWNER],
    items: [
      {
        label: "Properties",
        icon: HouseLineIcon,
        href: "/dashboard/properties",
      },
      { label: "Reviews", icon: StarIcon, href: "/dashboard/reviews" },
      {
        label: "Customer Support",
        icon: HeadsetIcon,
        href: "/dashboard/customer-support",
      },
    ],
  },

  {
    section: "Billing & Operations",
    roles: [Role.PROPERTY_OWNER],
    items: [
      {
        label: "Finance & Billing",
        icon: CreditCardIcon,
        href: "/dashboard/finance",
      },
      {
        label: "Work Orders",
        icon: ClipboardTextIcon,
        href: "/dashboard/work-orders",
      },
      { label: "Invoices", icon: ReceiptIcon, href: "/dashboard/invoices" },
    ],
  },

  // CONTRACTOR

  {
    section: "WORKSPACE",
    roles: [Role.CONTRACTOR, Role.AGENT],
    items: [
      { label: "Dashboard", icon: SquaresFourIcon, href: "/dashboard" },
      {
        label: "Jobs",
        icon: BriefcaseIcon,
        href: "/dashboard/jobs",
        permission: { resource: "JOBS", action: "read" },
      },
      {
        label: "Bids",
        icon: FilesIcon,
        href: "/dashboard/bids",
        permission: { resource: "BIDS", action: "read" },
      },
    ],
  },
  {
    section: "JOB MANAGEMENT",
    roles: [Role.CONTRACTOR, Role.AGENT],
    items: [
      {
        label: "Job Feed",
        icon: BinocularsIcon,
        href: "/dashboard/job-feed",
        permission: { resource: "BIDS", action: "read" },
      },
      {
        label: "Invitations",
        icon: HandshakeIcon,
        href: "/dashboard/invitations",
        permission: { resource: "BIDS", action: "read" },
      },
    ],
  },
  {
    section: "AGENT",
    roles: [Role.AGENT],
    items: [
      { label: "Dashboard", icon: SquaresFourIcon, href: "/dashboard/agent" },
      { label: "My Jobs", icon: BriefcaseIcon, href: "/dashboard/agent/jobs" },
      {
        label: "Invitations",
        icon: HandshakeIcon,
        href: "/dashboard/agent/dispatches",
      },
      {
        label: "Customer Support",
        icon: HeadsetIcon,
        href: "/dashboard/customer-support",
      },
    ],
  },

  {
    section: "TEAM",
    roles: [Role.CONTRACTOR, Role.AGENT],
    items: [
      {
        label: "My Organization",
        icon: UsersIcon,
        href: "/dashboard/team",
        permission: { resource: "TEAMS", action: "read" },
      },
      {
        label: "Roles",
        icon: AddressBookIcon,
        href: "/dashboard/roles",
        permission: { resource: "ROLES", action: "read" },
      },
    ],
  },

  // SHARED
  {
    section: "OTHERS",
    roles: [Role.CONTRACTOR, Role.AGENT],
    items: [
      {
        label: "Finance & Billing",
        icon: CreditCardIcon,
        href: "/dashboard/finance",
        permission: { resource: "FINANCIAL", action: "read" },
      },
      {
        label: "Reviews",
        icon: StarIcon,
        href: "/dashboard/reviews",
        permission: { resource: "REVIEWS", action: "read" },
      },
      {
        label: "Customer Support",
        icon: HeadsetIcon,
        href: "/dashboard/customer-support",
      },
    ],
  },

  // ADMIN
  {
    section: "WORKSPACE",
    roles: [Role.ADMIN],
    items: [
      { label: "Dashboard", icon: SquaresFourIcon, href: "/dashboard" },
      {
        label: "Team & Role Management",
        icon: UsersIcon,
        href: "/dashboard/admin/team-roles",
      },
    ],
  },
  {
    section: "JOB MANAGEMENT",
    roles: [Role.ADMIN],
    items: [
      { label: "Jobs", icon: BriefcaseIcon, href: "/dashboard/admin/jobs" },
      {
        label: "Disputes",
        icon: WarningIcon,
        href: "/dashboard/admin/disputes",
      },
    ],
  },
  {
    section: "USER MANAGEMENT",
    roles: [Role.ADMIN],
    items: [
      {
        label: "Users Directory",
        icon: AddressBookIcon,
        href: "/dashboard/admin/users",
      },
      {
        label: "Vetting Queue",
        icon: ArrowSquareInIcon,
        href: "/dashboard/admin/vetting",
      },
    ],
  },
  {
    section: "FINANCE",
    roles: [Role.ADMIN],
    items: [
      {
        label: "Transaction",
        icon: CurrencyCircleDollarIcon,
        href: "/dashboard/finance",
      },
      {
        label: "Ledger",
        icon: CalculatorIcon,
        href: "/dashboard/admin/ledger",
      },
      {
        label: "Wallet Management",
        icon: WalletIcon,
        href: "/dashboard/admin/account-balance-management",
      },
      {
        label: "Direct Payouts",
        icon: CurrencyCircleDollarIcon,
        href: "/dashboard/admin/direct-payouts",
      },
    ],
  },
  {
    section: "SETTINGS & AUDIT",
    roles: [Role.ADMIN],
    items: [
      {
        label: "Platform Settings",
        icon: GearIcon,
        href: "/dashboard/settings",
      },
      {
        label: "Audit Log",
        icon: ArchiveBoxIcon,
        href: "/dashboard/admin/audit-log",
      },
      {
        label: "AI Alerts",
        icon: SparkleIcon,
        href: "/dashboard/admin/ai-alerts",
      },
    ],
  },
];

export const getSidebarByRole = (role: Role) => {
  return sidebarConfig.filter((section) => section.roles.includes(role));
};

/** Field-tech agent shell — `/dashboard/agent/*` routes only. */
const AGENT_SHELL_SECTION = "AGENT";

export const getSidebarForUser = (user: User | null | undefined) => {
  if (!user) return [];
  const role = user.role as Role;
  const shell = getUserAppShell(user);

  if (role === Role.AGENT && shell === "agent") {
    return sidebarConfig.filter(
      (section) => section.section === AGENT_SHELL_SECTION,
    );
  }

  const sections = sidebarConfig.filter((section) => {
    if (!section.roles.includes(role)) return false;
    if (role === Role.AGENT && shell === "web") {
      return section.section !== AGENT_SHELL_SECTION;
    }
    return true;
  });

  if (role !== Role.AGENT) {
    return sections;
  }

  return sections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => {
        if (!item.permission) return true;
        return hasPermission(
          user,
          item.permission.resource,
          item.permission.action,
        );
      }),
    }))
    .filter((section) => section.items.length > 0);
};
